import { useState, useRef } from "react";
import API, { createController } from "../../../Network/API";
import { toast } from "react-hot-toast";
import { set } from "nprogress";

const REPORT_FILENAME_MAP = {
    0: "all_operator_sites",
    1: "rms_alarm_report",
    3: "dc_energy_consumption",
    4: "dc_kW_report",
    5: "pg_run_hour",
    6: "bts_log_history",
    7: "ac_energy_meter",
    8: "dc_kWh_report",
    9: "site_down_history",
    10: "overload_history",
    11: "temperature_report",
    12: "site_offline_report",
    13: "power_availability",
    14: "dc_availability",
    15: "rms_current_status",
    16: "availability_report",
    17: "rectifier_dc_kw_report",
    18: "availability_report_day_wise",
    19: "availability_with_events_report",
    20: "Outage_report",
    21: "Data_Available_Report",
    22: "acs_alarm_report",
    23: "Overload_report",
    24: "power_consumed_report",
    25: "all_sites",
    41: "avg_kw_report",
    42: "max_kw_report",
};

export default function useReportExport() {
    const [progress, setProgress] = useState(0);
    const [exportLoading, setExportLoading] = useState(false);
    const intervalRef = useRef();

    const getFileName = (form) => {
        const typePrefix = REPORT_FILENAME_MAP[form.report_type] || "report";
        let name = typePrefix + "_";
        if (form.site) name += `${form.site}_${form.start_date}`;
        else name += `${form.start_date}`;
        if (form.end_date) name += `_${form.end_date}`;
        const ext = [21, 3, 4].includes(form.report_type) ? ".csv" : ".xlsx";
        return name + ext;
    };

    const startFakeProgress = (setProgress) => {
        let progress = 0;

        const interval = setInterval(() => {
            progress += Math.random() * 5; // random speed
            if (progress >= 90) {
                progress = 90;
                clearInterval(interval);
            }
            setProgress(Math.floor(progress));
        }, 300);

        return interval;
    };

    const exportReport = async (form) => {
        if (!form.report_type) return toast.error("Please select report type");
        setExportLoading(true);
        setProgress(0);
        const controller = createController();
        const signal = controller.signal;

        const fd = new FormData();
        Object.entries({
            report_type: form.report_type,
            cluster: form.cluster,
            zone: form.zone,
            site: form.site,
            tenant_id: form.tenant,
            duration: form.duration_type,
            start_date: form.start_date,
            end_date: form.end_date,
        }).forEach(([k, v]) => fd.append(k, v || ""));

        form.rms_alarm.forEach((a, i) => fd.append(`alarms[${i}]`, a.value));
        form.acs_alarm.forEach((a, i) => fd.append(`alarms[${i}]`, a.value));

        // Async polling reports
        if ([1, 2, 18,19,22].includes(form.report_type)) {
            API.post("/v1/oauth/export_report", fd, { signal })
                .then((res) => pollReport(res.data.request_id, form))
                .catch((err) => {
                    if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError" || err?.message === "canceled") return;
                    toast.error(err.response?.data?.message || "Failed");
                    setExportLoading(false);
                    setProgress(0);
                });
            return;
        }

        // if ([4].includes(form.report_type)) {
        //     API.post("/v1/oauth/export_report", fd, { signal, responseType: "blob" })
        //         .then((res) => {
        //             setExportLoading(false);
        //             setProgress(100);
        //             const url = URL.createObjectURL(res.data);
        //             const a = document.createElement("a");
        //             a.href = url;
        //             a.download = getFileName(form);
        //             a.click();
        //             URL.revokeObjectURL(url);
        //         }
        //         )
        //         .catch((err) => {
        //             toast.error(err.response?.data?.message || "Failed");
        //             setExportLoading(false);
        //             setProgress(0);
        //         }
        //         );
        //     return;
        // }
        const fakeInterval = startFakeProgress(setProgress);
        // Synchronous download
        API.post("/v1/oauth/export_report", fd, { responseType: "blob", signal })

            .then((res) => {
                clearInterval(fakeInterval);

                setProgress(100);
                setExportLoading(false);

                const url = URL.createObjectURL(res.data);
                const a = document.createElement("a");
                a.href = url;
                a.download = getFileName(form);
                a.click();
                URL.revokeObjectURL(url);
            })
            .catch((err) => {
                clearInterval(fakeInterval);
                if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError" || err?.message === "canceled") return;
                setProgress(0);
                setExportLoading(false);
                toast.error(err.response?.data?.message || "Failed");
            });
    };

    const pollReport = (request_id, form) => {
        intervalRef.current = setInterval(async () => {
            try {
                const res = await API.get(`/v1/oauth/rms/rms_report/checkStatus?token=${request_id}`);
                // console.log(res.data.percent);
                if (res.data.percent == 100 && res.data.status == 0) {
                    setProgress(98);
                } else {
                    setProgress(res.data.percent);
                }

                if (res.data.status === 1) {
                    clearInterval(intervalRef.current);
                    downloadAsyncReport(request_id, form);
                    setExportLoading(false);
                    toast.success("Report generated successfully!");
                }
                if (res.data.status === 3) {
                    clearInterval(intervalRef.current);
                    setExportLoading(false);
                    setProgress(0);
                    toast.error("Report generation failed!");
                }
            } catch (err) {
                clearInterval(intervalRef.current);
                setExportLoading(false);
                setProgress(0);
                toast.error("Error generating report");
            }
        }, 6000); // poll every 6 seconds
    };

    const downloadAsyncReport = async (token, form) => {
        try {
            const res = await API.get(`/v1/oauth/rms/reports/download?token=${token}`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = getFileName(form);
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error("Failed to download report");
            setExportLoading(false);
        }
    };

    return { progress, exportLoading, exportReport };
}
